/**
 * src/hooks/useCurrentUser.ts
 *
 * Single source of truth for the /api/auth/me query.
 * Both the _authenticated route guard (beforeLoad via ensureQueryData)
 * and the AppShell component (via useQuery) use the same queryKey and
 * config so TanStack Query serves AppShell from cache — no duplicate
 * network request.
 */
import { queryOptions, useQuery } from "@tanstack/react-query";
import { getMe } from "@/integrations/api/auth";
import type { MeResult } from "@/integrations/api/auth";

export const currentUserQueryOptions = () =>
  queryOptions<MeResult>({
    queryKey: ["current-user"],
    queryFn: getMe,
    staleTime: 5 * 60 * 1000,   // 5 minutes — me changes rarely; avoids refetch on every navigation
    gcTime: Infinity,             // keep in cache until explicitly cleared (e.g. on logout)
    refetchOnWindowFocus: false,  // do not refetch when user switches tabs
    refetchOnReconnect: false,
    retry: false,
  });

/** React hook for components that need current user data */
export function useCurrentUser() {
  return useQuery(currentUserQueryOptions());
}
